"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useDismissedChecks } from "@/hooks/useDismissedChecks";
import { useAICheckStore } from "@/stores/aicheck";
import type { AICheckRunResult, FocusedEvidenceTarget } from "@/types/aicheck";
import { CheckItemCard } from "./shared/CheckItemCard";
import {
  CheckItemDetailEmpty,
  CheckItemDetailPanel,
} from "./shared/CheckItemDetailPanel";
import { StatusFilterButtons } from "./shared/StatusFilterButtons";
import { scrollElementIntoContainer } from "./shared/scroll-utils";
import { compareByStatusAndImportance } from "./shared/sort-utils";

interface NeedReviewViewProps {
  result: AICheckRunResult;
  onReturnToSummary?: (findingIndex: number) => void;
  onProjectDataReferenceClick?: (source: string) => void;
}

interface ReviewItem {
  id: string;
  title: string;
  detail: string;
  severity: "high" | "medium" | "low";
  status: string;
  importance: string;
  score?: number;
  rawAIScore?: number;
  scoreAdjusted?: boolean;
  rawResult?: Record<string, unknown>;
  evaluationError?: boolean;
  source: "ai" | "rule";
}

export function NeedReviewView({
  result,
  onReturnToSummary,
  onProjectDataReferenceClick,
}: NeedReviewViewProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingScrollCheckId, setPendingScrollCheckId] = useState<
    string | null
  >(null);
  const [pendingEvidenceTarget, setPendingEvidenceTarget] =
    useState<FocusedEvidenceTarget | null>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const { dismiss, restore, isDismissed, dismissedCount } = useDismissedChecks(
    result.projectId,
  );
  const [hideDismissed, setHideDismissed] = useState(false);
  const focusedCheckId = useAICheckStore((state) => state.focusedCheckId);
  const focusedEvidenceTarget = useAICheckStore(
    (state) => state.focusedEvidenceTarget,
  );
  const highlightCheckId = useAICheckStore((state) => state.highlightCheckId);
  const highlightEvidenceTarget = useAICheckStore(
    (state) => state.highlightEvidenceTarget,
  );
  const summaryReturnTarget = useAICheckStore(
    (state) => state.summaryReturnTarget,
  );
  const setFocusedCheckId = useAICheckStore((state) => state.setFocusedCheckId);
  const setFocusedEvidenceTarget = useAICheckStore(
    (state) => state.setFocusedEvidenceTarget,
  );
  const setHighlightCheckId = useAICheckStore(
    (state) => state.setHighlightCheckId,
  );
  const setHighlightEvidenceTarget = useAICheckStore(
    (state) => state.setHighlightEvidenceTarget,
  );
  const setSummaryReturnTarget = useAICheckStore(
    (state) => state.setSummaryReturnTarget,
  );
  const reviewListRef = useRef<HTMLDivElement>(null);

  const reviewItems = useMemo(() => {
    const items: ReviewItem[] = [];

    for (const r of result.aiResults.filter(
      (r) => r.category === "need_review",
    )) {
      items.push({
        id: r.checkId,
        title: r.checkName,
        detail: r.summary ?? r.error ?? "No details available",
        severity:
          r.status === "fail" || r.status === "error"
            ? "high"
            : r.status === "warning"
              ? "medium"
              : "low",
        status: r.status,
        importance: r.importance ?? "medium",
        score: r.overallScore,
        rawAIScore: r.rawAIScore,
        scoreAdjusted: r.scoreAdjusted,
        rawResult: r.result,
        source: "ai",
      });
    }

    for (const r of result.consistencyResults.filter(
      (r) => r.category === "need_review",
    )) {
      items.push({
        id: r.ruleId,
        title: r.ruleName,
        detail: r.message,
        severity: !r.passed
          ? r.severity === "error"
            ? "high"
            : "medium"
          : "low",
        status: r.evaluationError ? "error" : r.passed ? "pass" : r.severity,
        importance: r.importance ?? "medium",
        evaluationError: r.evaluationError,
        source: "rule",
      });
    }

    items.sort(compareByStatusAndImportance);
    return items;
  }, [result]);

  const errorItems = useMemo(
    () => reviewItems.filter((item) => item.status === "error"),
    [reviewItems],
  );

  const normalItems = useMemo(
    () => reviewItems.filter((item) => item.status !== "error"),
    [reviewItems],
  );

  const statusOptions = useMemo(() => {
    const order = ["fail", "warning", "pass"];
    const counts = new Map<string, number>();
    for (const item of normalItems) {
      if (!isDismissed(item.id)) {
        counts.set(item.status, (counts.get(item.status) ?? 0) + 1);
      }
    }
    return order
      .filter((status) => counts.has(status))
      .map((status) => ({ status, count: counts.get(status) ?? 0 }));
  }, [normalItems, isDismissed]);

  const filteredItems = useMemo(() => {
    if (statusFilter.length === 0) return normalItems;
    return normalItems.filter((item) => statusFilter.includes(item.status));
  }, [normalItems, statusFilter]);

  const { activeItems, dismissedItems } = useMemo(() => {
    const active: ReviewItem[] = [];
    const dismissed: ReviewItem[] = [];
    for (const item of filteredItems) {
      if (isDismissed(item.id)) dismissed.push(item);
      else active.push(item);
    }
    return { activeItems: active, dismissedItems: dismissed };
  }, [filteredItems, isDismissed]);

  const activeCount = normalItems.filter((i) => !isDismissed(i.id)).length;

  const selectedItem = reviewItems.find((i) => i.id === selectedId) ?? null;
  const isPendingScrollVisible = useMemo(() => {
    if (!pendingScrollCheckId) return false;
    if (errorItems.some((item) => item.id === pendingScrollCheckId)) {
      return true;
    }
    if (activeItems.some((item) => item.id === pendingScrollCheckId)) {
      return true;
    }
    return (
      !hideDismissed &&
      dismissedItems.some((item) => item.id === pendingScrollCheckId)
    );
  }, [
    activeItems,
    dismissedItems,
    errorItems,
    hideDismissed,
    pendingScrollCheckId,
  ]);

  useEffect(() => {
    if (!selectedId) return;
    const stillVisible =
      activeItems.some((item) => item.id === selectedId) ||
      dismissedItems.some((item) => item.id === selectedId) ||
      errorItems.some((item) => item.id === selectedId);
    if (!stillVisible) setSelectedId(null);
  }, [activeItems, dismissedItems, selectedId, errorItems]);

  useEffect(() => {
    if (
      !focusedCheckId ||
      !reviewItems.some((item) => item.id === focusedCheckId)
    ) {
      return;
    }

    if (statusFilter.length > 0) {
      setStatusFilter([]);
    }
    if (hideDismissed && isDismissed(focusedCheckId)) {
      setHideDismissed(false);
    }

    setSelectedId(focusedCheckId);
    setHighlightCheckId(focusedCheckId);
    setPendingScrollCheckId(focusedCheckId);
    setFocusedCheckId(null);
  }, [
    focusedCheckId,
    hideDismissed,
    isDismissed,
    reviewItems,
    setFocusedCheckId,
    setHighlightCheckId,
    statusFilter,
  ]);

  useEffect(() => {
    if (!pendingScrollCheckId || !isPendingScrollVisible) return;

    const element = document.getElementById(
      `check-card-${pendingScrollCheckId}`,
    );
    if (!element || !reviewListRef.current) {
      return;
    }

    const rafId = requestAnimationFrame(() => {
      scrollElementIntoContainer(reviewListRef.current, element);
      setPendingScrollCheckId((current) =>
        current === pendingScrollCheckId ? null : current,
      );
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isPendingScrollVisible, pendingScrollCheckId]);

  useEffect(() => {
    if (
      !focusedEvidenceTarget ||
      !reviewItems.some((item) => item.id === focusedEvidenceTarget.checkId)
    ) {
      return;
    }

    if (statusFilter.length > 0) {
      setStatusFilter([]);
    }
    if (hideDismissed && isDismissed(focusedEvidenceTarget.checkId)) {
      setHideDismissed(false);
    }

    setSelectedId(focusedEvidenceTarget.checkId);
    setHighlightCheckId(focusedEvidenceTarget.checkId);
    setHighlightEvidenceTarget(focusedEvidenceTarget);
    setPendingScrollCheckId(focusedEvidenceTarget.checkId);
    setPendingEvidenceTarget(focusedEvidenceTarget);
    setFocusedEvidenceTarget(null);
  }, [
    focusedEvidenceTarget,
    hideDismissed,
    isDismissed,
    reviewItems,
    setFocusedEvidenceTarget,
    setHighlightCheckId,
    setHighlightEvidenceTarget,
    statusFilter,
  ]);

  useEffect(() => {
    if (
      !highlightCheckId ||
      !reviewItems.some((item) => item.id === highlightCheckId)
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      setHighlightCheckId(null);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [highlightCheckId, reviewItems, setHighlightCheckId]);

  useEffect(() => {
    if (
      !highlightEvidenceTarget ||
      !reviewItems.some((item) => item.id === highlightEvidenceTarget.checkId)
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      setHighlightEvidenceTarget(null);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [highlightEvidenceTarget, reviewItems, setHighlightEvidenceTarget]);

  function handleManualSelect(checkId: string) {
    setSummaryReturnTarget(null);
    setPendingEvidenceTarget(null);
    setHighlightEvidenceTarget(null);
    setSelectedId((current) => (current === checkId ? null : checkId));
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 animate-in fade-in duration-500 lg:h-[600px]">
      <div className="flex-1 min-h-0">
        {reviewItems.length === 0 ? (
          <div className="flex items-center justify-center min-h-[200px] text-gray-400">
            <p className="text-sm">No items to review.</p>
          </div>
        ) : (
          <div
            ref={reviewListRef}
            className="flex flex-col gap-3 overflow-auto lg:h-full pr-1"
          >
            {errorItems.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-red-700">
                  Evaluation Errors
                </div>
                <div className="space-y-2">
                  {errorItems.map((item) => (
                    <button
                      type="button"
                      key={item.id}
                      id={`check-card-${item.id}`}
                      onClick={() => handleManualSelect(item.id)}
                      className={`group relative flex items-start gap-3 rounded-md border p-3 text-left transition-all cursor-pointer w-full ${
                        highlightCheckId === item.id
                          ? "border-amber-300 bg-white ring-2 ring-amber-300 shadow-md"
                          : "border-red-200 bg-white hover:border-red-300 hover:shadow-sm"
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-sm text-red-800 group-hover:text-red-900 transition-colors">
                            {item.title}
                          </h4>
                        </div>
                        <p className="text-xs text-red-700 line-clamp-2">
                          {item.detail || "Evaluation failed."}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <StatusFilterButtons
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              statusOptions={statusOptions}
              activeCount={activeCount}
              dismissedCount={dismissedCount}
              hideDismissed={hideDismissed}
              setHideDismissed={setHideDismissed}
            />
            {activeItems.map((item) => (
              <CheckItemCard
                key={item.id}
                id={item.id}
                title={item.title}
                detail={item.detail}
                severity={item.severity}
                status={item.status}
                importance={item.importance}
                score={item.score}
                rawAIScore={item.rawAIScore}
                scoreAdjusted={item.scoreAdjusted}
                source={item.source}
                isSelected={selectedId === item.id}
                isHighlighted={highlightCheckId === item.id}
                isDismissed={false}
                onSelect={() => handleManualSelect(item.id)}
                onDismiss={() => dismiss(item.id)}
                onRestore={() => restore(item.id)}
              />
            ))}
            {!hideDismissed && dismissedItems.length > 0 && (
              <div className="opacity-40 space-y-3 mt-4 pt-4 border-t border-dashed border-zinc-300">
                {dismissedItems.map((item) => (
                  <CheckItemCard
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    detail={item.detail}
                    severity={item.severity}
                    status={item.status}
                    importance={item.importance}
                    score={item.score}
                    rawAIScore={item.rawAIScore}
                    scoreAdjusted={item.scoreAdjusted}
                    source={item.source}
                    isSelected={selectedId === item.id}
                    isHighlighted={highlightCheckId === item.id}
                    isDismissed={true}
                    onSelect={() => handleManualSelect(item.id)}
                    onDismiss={() => dismiss(item.id)}
                    onRestore={() => restore(item.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="lg:w-[420px] xl:w-[540px]">
        {selectedItem ? (
          <CheckItemDetailPanel
            title={selectedItem.title}
            detail={selectedItem.detail}
            status={selectedItem.status}
            importance={selectedItem.importance}
            score={selectedItem.score}
            rawAIScore={selectedItem.rawAIScore}
            scoreAdjusted={selectedItem.scoreAdjusted}
            source={selectedItem.source}
            rawResult={selectedItem.rawResult}
            projectData={result.projectData}
            contactTopics={result.contactTopicsResult?.contactTopics}
            isDismissed={isDismissed(selectedItem.id)}
            onDismiss={() => dismiss(selectedItem.id)}
            onRestore={() => restore(selectedItem.id)}
            onClose={() => setSelectedId(null)}
            headerAction={
              summaryReturnTarget?.checkId === selectedItem.id ? (
                <button
                  type="button"
                  onClick={() =>
                    onReturnToSummary?.(summaryReturnTarget.findingIndex)
                  }
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  ← Executive Summary
                </button>
              ) : null
            }
            evidenceIdPrefix={`check-evidence-${selectedItem.id}`}
            focusedEvidenceIndex={
              pendingEvidenceTarget?.checkId === selectedItem.id
                ? pendingEvidenceTarget.evidenceIndex
                : null
            }
            highlightedEvidenceIndex={
              highlightEvidenceTarget?.checkId === selectedItem.id
                ? highlightEvidenceTarget.evidenceIndex
                : null
            }
            onProjectDataReferenceClick={onProjectDataReferenceClick}
            onFocusedEvidenceHandled={() =>
              setPendingEvidenceTarget((current) =>
                current?.checkId === selectedItem.id ? null : current,
              )
            }
            feedbackContext={{
              // AICheckRunResult does not declare runId, but cached/streamed
              // run results may carry it at runtime — forward when present.
              runId: (result as { runId?: string }).runId,
              // Guard: never send the literal string "undefined"
              projectId:
                result.projectId != null ? String(result.projectId) : undefined,
              checkId: selectedItem.id,
            }}
          />
        ) : (
          <CheckItemDetailEmpty />
        )}
      </div>
    </div>
  );
}
